import { useLocalStorage } from '@gravitate-js/excalibrr'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useInvoices } from '../../../../api/useFreight/useInvoices'
import { useGridFilters, type CostRevenueType } from '../useGridFilters'

export type UndefinedDate = Date | undefined

// Constants
const INVOICE_DATE_RANGE_KEY = 'invoice_date_range'

// Helper function for search filter handling
const handleSearchFilters = (filters: URLSearchParams) => {
  const searchParams = new URLSearchParams(filters)
  const searchParamsObject = Object.fromEntries(searchParams.entries())

  // Remove primary parameters that are handled separately
  delete searchParamsObject.status
  delete searchParamsObject.view
  delete searchParamsObject.from_date
  delete searchParamsObject.to_date
  delete searchParamsObject.booktype

  return searchParamsObject
}

/**
 * Custom hook for managing invoice list state
 * Handles loading, filtering, and view mode for invoices
 */
export function useInvoiceState() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [serverFilters, setServerFilters] = useState(handleSearchFilters(searchParams))

  // Date range state with localStorage persistence and URL parameter override
  const [sentDateRangeFilter, setSentDateRangeFilter] = useState<[Date, Date]>(() => {
    // Check for URL parameter overrides first
    const fromDateParam = searchParams.get('from_date')
    const toDateParam = searchParams.get('to_date')

    if (fromDateParam && toDateParam) {
      try {
        const fromDate = new Date(fromDateParam)
        const toDate = new Date(toDateParam)
        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
          return [fromDate, toDate]
        }
      } catch (error) {
        console.warn('Invalid date parameters in URL:', { fromDateParam, toDateParam })
      }
    }

    const savedRange = localStorage.getItem(INVOICE_DATE_RANGE_KEY)
    if (savedRange) {
      const [start, end] = JSON.parse(savedRange)
      return [new Date(start), new Date(end)]
    }

    // Default to last 7 days if no saved range
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)
    return [start, end]
  })

  // View style state (grouped or flat)
  const [selectedViewStyle, setSelectedViewStyle] = useState<'grouped' | 'flat'>('grouped')

  // Get all filter functions from the dedicated filter hook
  const { activeStatus, setActiveStatus, costRevenue, setCostRevenue, getFilteredData, getStatusCounts } =
    useGridFilters()

  // API query for invoice data
  const { useInvoicesQuery } = useInvoices()
  const { data: invoicesData, isLoading } = useInvoicesQuery({
    start_date: sentDateRangeFilter[0],
    end_date: sentDateRangeFilter[1],
    filter: serverFilters,
  })

  // Handle date range changes
  const handleDateRangeChange = (dates: [Date, Date]) => {
    setSentDateRangeFilter(dates)
    if (dates[0] && dates[1]) {
      localStorage.setItem(INVOICE_DATE_RANGE_KEY, JSON.stringify([dates[0].toISOString(), dates[1].toISOString()]))
    }
  }

  // Process URL parameters on initial load and changes
  useEffect(() => {
    if (searchParams.size === 0) return

    const searchParamsFromUrl = new URLSearchParams(searchParams)
    const searchParamsObject = Object.fromEntries(searchParamsFromUrl.entries())

    let shouldClearParams = false

    // Handle booktype parameter
    if (searchParamsObject.booktype) {
      const bookTypeValue = searchParamsObject.booktype as CostRevenueType
      if (['Cost', 'Revenue', 'All'].includes(bookTypeValue)) {
        setCostRevenue(bookTypeValue)
        shouldClearParams = true
      }
    }

    // Handle date parameter overrides
    const fromDateParam = searchParamsObject.from_date
    const toDateParam = searchParamsObject.to_date

    if (fromDateParam && toDateParam) {
      try {
        const fromDate = new Date(fromDateParam)
        const toDate = new Date(toDateParam)
        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
          setSentDateRangeFilter([fromDate, toDate])
          shouldClearParams = true
        }
      } catch (error) {
        console.warn('Invalid date parameters in URL:', { fromDateParam, toDateParam })
      }
    }

    // Set active status from URL param
    if (searchParamsObject.status) {
      if (searchParamsObject.status === 'open') {
        setActiveStatus('all')
      } else {
        setActiveStatus(searchParamsObject.status)
      }
      shouldClearParams = true
    }

    // Set view style from URL param
    if (searchParamsObject.view) {
      setSelectedViewStyle(searchParamsObject.view as 'grouped' | 'flat')
      shouldClearParams = true
    }

    // Clear URL parameters after applying them
    if (shouldClearParams) {
      setSearchParams({})
    }
  }, [searchParams, setSearchParams, setCostRevenue, setActiveStatus])

  // Apply filters using the hook
  const filteredData = useMemo(() => {
    return getFilteredData(invoicesData?.rows)
  }, [invoicesData, getFilteredData])

  // Calculate status counts using the hook
  const statusCounts = useMemo(() => {
    return getStatusCounts(invoicesData?.rows)
  }, [invoicesData, getStatusCounts])

  return {
    // State
    isLoading,
    activeStatus,
    costRevenue,
    sentDateRangeFilter,
    selectedViewStyle,
    filteredData,
    statusCounts,
    serverFilters,
    invoicesData,

    // Actions
    setActiveStatus,
    setCostRevenue,
    handleDateRangeChange,
    setSelectedViewStyle,
    setServerFilters,
  }
}
